import React, {useEffect, useState, useMemo} from 'react';
import QualificationChart from '../../../Components/Charts/QualificationChart';
import ChangeMonthCalendar from '../../MediaAndNews/ChangeMonthCalendar';
import {useTranslation} from 'react-i18next';
import i18n from '../../../locales/i18n';
import {formatNumber} from '../../../assets/utils/utils';
import icon from '../../../assets/img/icons/actions/info.svg';
import {toast} from 'react-toastify';
import {monthNow} from '../../../assets/utils/dateUtils';
import {useGetCommandVolumeQuery} from '../../../redux/myDynamicApi';
import Loader from '../../../assets/Animations/PreLoaderComponent/Loader';
import styles from './VolumeCommand.module.css';
import {StatsElement} from '../../../types/MyDynamicsData';
import MonthChangeButton from './MonthChangeButton';
import ChartIndicators from './ChartIndicators';

const capitalize = (value: string) =>
	value ? value.charAt(0).toUpperCase() + value.slice(1) : value;

const formatMonthLabel = (value: string) => {
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) {
		return value;
	}
	const lang = i18n.language || 'uk';
	const locale = lang.startsWith('uk') ? 'uk-UA' : 'en-US';
	const options: Intl.DateTimeFormatOptions = {month: 'long', year: 'numeric'};
	const label = date.toLocaleString(locale, options);
	return capitalize(label);
};

// Функция для определения, является ли выбранный месяц текущим
const isCurrentMonth = (selectedMonth: string): boolean => {
	const currentMonth = monthNow();
	const selectedDate = new Date(selectedMonth);
	const currentDate = new Date(currentMonth);
	return (
		selectedDate.getFullYear() === currentDate.getFullYear() &&
		selectedDate.getMonth() === currentDate.getMonth()
	);
};

// Функция для получения текущего дня месяца
const getCurrentDay = (): number => {
	return new Date().getDate();
};

// Функция для получения предыдущего месяца относительно выбранного
const getPreviousMonth = (selectedMonth: string): string => {
	const selectedDate = new Date(selectedMonth);
	const previousDate = new Date(selectedDate);
	previousDate.setMonth(previousDate.getMonth() - 1);
	const year = previousDate.getFullYear();
	const month = (previousDate.getMonth() + 1).toString().padStart(2, '0');
	const day = '01';
	return `${year}-${month}-${day}`;
};

// Функция для выбора правильного элемента из stats массива
const getSelectedStatElement = (
	stats: StatsElement[] | undefined,
	selectedMonth: string,
): StatsElement | null => {
	if (!stats || stats.length === 0) {
		return null;
	}

	if (isCurrentMonth(selectedMonth)) {
		// Для текущего месяца - находим элемент с stat_date равным текущему дню
		const currentDay = getCurrentDay();
		const foundElement = stats.find((stat) => stat.stat_date === currentDay);
		// Если не нашли точное совпадение, берем последний элемент с stat_date <= currentDay
		if (!foundElement) {
			const filtered = stats.filter((stat) => stat.stat_date <= currentDay);
			return filtered.length > 0
				? filtered[filtered.length - 1]
				: stats[stats.length - 1];
		}
		return foundElement;
	} else {
		// Для прошлых месяцев - берем последний элемент
		return stats[stats.length - 1];
	}
};

type VolumeCommandProps = {
	month?: string;
	onMonthChange?: (value: string) => void;
};

const VolumeCommand: React.FC<VolumeCommandProps> = ({
	month,
	onMonthChange,
}) => {
	const {t} = useTranslation();
	//const [showCalendar, setShowCalendar] = useState(false);
	const [internalMonth, setInternalMonth] = useState<string>(
		month ?? monthNow(),
	);
	const controlledMonth = month ?? internalMonth;
	const [currentMonthName, setCurrentMonthName] = useState<string>(() =>
		formatMonthLabel(controlledMonth),
	);

	useEffect(() => {
		if (month) {
			setCurrentMonthName(formatMonthLabel(month));
		}
	}, [month]);

	const infoText = t(
		'Різниця сумарного по дням командного обсягу між поточним місяцем та цього самого дня минулого місяця',
	);
	const handleClickInfo = () => {
		toast(infoText, {
			className: 'volume-command-toast',
		});
	};
	const {
		data: volumeCommand,
		isLoading,
		isFetching: isFetchingVolumeCommand,
	} = useGetCommandVolumeQuery(controlledMonth);

	// Получаем выбранный элемент статистики на основе текущего дня или последнего дня
	// Важно: useMemo должен быть вызван до любого условного возврата
	const selectedStatElement = useMemo(() => {
		return getSelectedStatElement(volumeCommand?.stats, controlledMonth);
	}, [volumeCommand?.stats, controlledMonth]);

	// Вычисляем названия месяцев для отображения
	const currentMonthLabel = useMemo(() => {
		return formatMonthLabel(controlledMonth);
	}, [controlledMonth]);

	const previousMonthLabel = useMemo(() => {
		return formatMonthLabel(getPreviousMonth(controlledMonth));
	}, [controlledMonth]);

	if (isLoading) return <Loader size={'large'} />;

	const handleSetMonth = (value: string) => {
		if (!month) {
			setInternalMonth(value);
		}
		onMonthChange?.(value);
		setCurrentMonthName(formatMonthLabel(value));
	};

	return (
		<div className={`page__chart ${styles.chart}`}>
			<div className={styles.chartHeader}>
				<div className={styles.chartRow}>
					<div className={styles.chartColumn}>
						<div className={styles.chartVolumeRow}>
							<div className={`${styles.chartVolume} name`}>
								{t('Командний ТО')}
							</div>
							<div className={styles.chartColumn}>
								<div className={`${styles.dataChart}`}>
									<div
										className={`${styles.dataChartItem} ${styles.dataChartItemCurrent}`}
									>
										{currentMonthLabel}
									</div>
									<div className={styles.dataChartItem}>
										{previousMonthLabel}
									</div>
								</div>
							</div>
							<MonthChangeButton
								isFetchingVolumeCommand={isFetchingVolumeCommand}
								currentMonthName={currentMonthName}
								setCurrentMonthName={setCurrentMonthName}
								handleSetMonth={handleSetMonth}
							/>
						</div>
						<div className={styles.chartRow}>
							{selectedStatElement && (
								<div className={styles.chartRate}>
									{formatNumber(selectedStatElement.nowm_points_total)}{' '}
									{t('Бали')}{' '}
									<span>
										{selectedStatElement.div_total > 0 ? '+' : ''}
										{selectedStatElement.div_total}%
										<button onClick={handleClickInfo} title={infoText}>
											<img src={icon} alt={'info'} />
										</button>
									</span>
								</div>
							)}
							<ChartIndicators selectedStatElement={selectedStatElement} />
						</div>
					</div>
				</div>
			</div>
			{volumeCommand && volumeCommand.stats && (
				<div className={`${styles.chartMain} main-chart`}>
					<QualificationChart
						stat={volumeCommand.stats}
						currentMonthLabel={currentMonthLabel}
						previousMonthLabel={previousMonthLabel}
					/>
				</div>
			)}
		</div>
	);
};

export default VolumeCommand;
